import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { InvoiceCardProps } from "./types";
import { TextField } from "ui/components/TextField/TextField";
import { Button } from "ui/components/Button/Button";
import InvoiceEditCard from "./components/InvoiceEditCard/InvoiceEditCard";
import { useDispatch, useSelector } from "react-redux";
import { Setting, FiscalizationCertificateInfo } from "services/api/settings/types";
import { getFiscalizationCertificateAPI } from "services/api/settings/fiscalization/api";
import { getSettings } from "services/api/settings/redux/selectors";
import { initialSettingEditForm } from "services/api/settings";
import { fetchSettings } from "services/api/settings/redux";
import { Badge } from "ui/components/Badge";

const InvoiceCard: React.FC<InvoiceCardProps> = () => {

  const { t } = useTranslate();
  const dispatch = useDispatch();
  const settingCopy = useSelector(getSettings);
  const [setting, setSetting] = useState<Setting>(initialSettingEditForm);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [certInfo, setCertInfo] = useState<FiscalizationCertificateInfo | null>(null);

  useEffect(() => {
    setSetting(settingCopy);
  }, [settingCopy]);

  useEffect(() => {
    const fetchCert = async () => {
      try {
        const result = await getFiscalizationCertificateAPI();
        setCertInfo(result);
      } catch {}
    };
    fetchCert();
  }, []);

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = useCallback(() => {
    dispatch(fetchSettings() as any);
    setShowEditModal(false);
  }, []);


  return (
    <>
      {/* Računi */}
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
        <div className="flex flex-col flex-1 max-w-[300px]">
          <p className="text-base font-bold text-slate-600 py-3">
            3. Računi
          </p>
          <p className="text-sm">
            Postavke za izlazne račune
          </p>
        </div>
        <div className="flex justify-between items-start flex-1 px-3 my-3">
          <div className="flex flex-col w-[400px]">
            <div className="mb-3">
              <TextField
                type="text"
                name="invoiceFormatNumber"
                label="Format broja računa"
                placeholder="Format broja računa"
                value={settingCopy.invoiceFormatNumber}
                disabled
              />
            </div>
            <div className="mb-3">
              <ul className="common-list text-sm">
                <li>{t("{YYYY} - godina kreiranja računa")}</li>
                <li>{t("{YY} - zadnje dvije znamenke godine kreiranja računa")}</li>
                <li>{t("{MM} - mjesec kreiranja računa")}</li>
                <li>{t("{DD} - dan kreiranja računa")}</li>
                <li>{t("{ID} - Identifikator računa u toj godini")}</li>
              </ul>
            </div>
             <div className="mb-3">
              <TextField
                type="text"
                name="invoicePaymentIBAN"
                label="IBAN za uplatu"
                placeholder="IBAN za uplatu"
                value={settingCopy.invoicePaymentIBAN}
                disabled
              />
            </div>
          </div>
          <div>
            <Button
              variant="outlined"
              children={t("admin.edit")}
              onClick={handleEditClick}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </div>
      </div>

      {/* Fiskalizacija */}
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
        <div className="flex flex-col flex-1 max-w-[300px]">
          <p className="text-base font-bold text-slate-600 py-3">
            3.1 Fiskalizacija
          </p>
          <p className="text-sm">
            Postavke za fiskalizaciju računa
          </p>
        </div>
        <div className="flex flex-1 px-3 my-3">
          <div className="flex flex-col gap-3 w-[400px]">
            <div>
              <TextField
                type="text"
                name="tenantOib"
                label="OIB tvrtke"
                value={settingCopy.tenantOib}
                disabled
              />
            </div>
            <div>
              <TextField
                type="text"
                name="operaterOib"
                label="OIB operatera"
                value={settingCopy.operaterOib}
                disabled
              />
            </div>
            <div>
              <TextField
                type="text"
                name="businessUnitMark"
                label="Oznaka poslovnog prostora"
                value={settingCopy.businessUnitMark}
                disabled
              />
            </div>
            <div>
              <TextField
                type="text"
                name="cashRegisterMark"
                label="Oznaka naplatnog uređaja"
                value={settingCopy.cashRegisterMark}
                disabled
              />
            </div>
            <div className="flex items-center gap-2">
              <Badge
                text={settingCopy.fiscalizationDemoMode ? "Test (Demo)" : "Produkcija"}
                variant={settingCopy.fiscalizationDemoMode ? "warning" : "success"}
                showDot
              />
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-600">U sustavu PDV-a: </span>
              <span>{settingCopy.inVatSystem ? "Da" : "Ne"}</span>
            </div>
            <div className="text-sm mt-2">
              <span className="font-medium text-slate-600">Certifikat: </span>
              {certInfo && certInfo.hasCertificate ? (
                <Badge text={certInfo.subject || "Aktivan"} variant="success" height="small" />
              ) : (
                <Badge text="Nije konfiguriran" variant="danger" height="small" />
              )}
            </div>
          </div>
        </div>
      </div>

      <InvoiceEditCard
        show={showEditModal}
        setting={setting}
        setSetting={setSetting}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEditModal(false)}
      />
    </>
  );
};
export default memo(InvoiceCard);
